/**
 * clickable-fitment.js — PDP fitment behaviour (Fidelity-01 restructure;
 * contract per kit §39/§43 + Tasks 03/04b/04c, canon per page-pdp-states +
 * PageProductBelow).
 *
 * Reads state once on load, subscribes to 'clickable:vehicle-changed'.
 * CANONICAL CONTRACT: product TAGS ALONE decide fit — accentuate never votes
 * on the verdict; it supplies warning/helpful text and the table rows.
 * Match = case-insensitive full-string equality; FIRST MATCH WINS.
 *
 * Paints: the reserved message slot (helpful teal above critical amber —
 * canon lines), the red context band + in-column CTA (hard block: form and
 * quantity fully ABSENT on mismatch — no purchase path), the Confirmed-fit
 * card (state band + matching body/build rows), the FITMENTS accordion
 * (matched group floats to top, opens, YOUR CAR pill, row highlight,
 * per-row warning reveals), the reminder line, and — launch-critical —
 * injects properties[Warning] + properties[Car] into the product form.
 * Appends /{activeVehicleTag} to the breadcrumb collection link.
 * All metafield text is inserted via textContent — data, never markup.
 */
(function () {
  'use strict';

  var state = { name: null, data: null, matchIndex: -1, tags: [], generic: false, obwPool: null };

  function readData() {
    var el = document.querySelector('[data-fitment-data]');
    if (!el) return null;
    try {
      return JSON.parse(el.textContent);
    } catch (error) {
      return null;
    }
  }

  function readTags() {
    var el = document.querySelector('[data-vehicle-tags]');
    if (!el) return [];
    try {
      var tags = JSON.parse(el.textContent);
      return Array.isArray(tags) ? tags : [];
    } catch (error) {
      return [];
    }
  }

  /* LOCKSTEP DELEGATION (21 Aug): comparison lives ONLY in
     clickable-fit-predicate.js (sync head, fourth lockstep member) —
     thin delegates, fail-closed when the module is absent. */
  function firstMatchIndex(models, name) {
    return window.clickableFitPredicate
      ? window.clickableFitPredicate.firstMatchIndex(models, name) : -1;
  }

  function entryAt(list, index) {
    if (index < 0) return '';
    return window.clickableFitPredicate
      ? window.clickableFitPredicate.entryAt(list, index) : '';
  }

  function sameVehicle(a, b) {
    return !!window.clickableFitPredicate &&
      window.clickableFitPredicate.sameVehicle(a, b);
  }

  // Condensed vehicle display name (round-two addition 2) -- PRESENTATION
  // ONLY: state/properties keep the full canonical string everywhere.
  // Strip the build-range parenthetical always; strip BMW's redundant
  // "N Series" token (the chassis code carries it); VW/Audi series words
  // stay (load-bearing: "Volkswagen Golf Mk7.5 R", "Audi A3 8V 1.4 TFSI").
  function shortName(name) {
    if (!name) return name;
    var out = name.replace(/\s*\([^)]*\)/g, '');
    out = out.replace(/^BMW \d Series /, 'BMW ');
    // R6 2b (Lucas, 18 Aug): the make renders short — Volkswagen -> VW.
    // BMW / Audi / Skoda unchanged; full-name lines keep the canonical
    // string (this is PRESENTATION ONLY, as ever).
    out = out.replace(/^Volkswagen /, 'VW ');
    return out.replace(/\s+/g, ' ').trim();
  }
  window.clickableShortVehicleName = shortName;

  function groupOf(model) {
    var cut = model.indexOf(') ');
    return cut === -1 ? model : model.slice(0, cut + 1);
  }

  function showBox(which) {
    document.querySelectorAll('[data-fit-box]').forEach(function (box) {
      if (box.getAttribute('data-fit-box') === which) box.removeAttribute('hidden');
      else box.setAttribute('hidden', '');
    });
  }

  function clearInjectedProperties() {
    document.querySelectorAll('[data-clickable-fitment-property]').forEach(function (el) {
      el.remove();
    });
  }

  // The flag follows the item (Build Ref §1a): hidden line-item properties on
  // the product form carry the matched warning + vehicle into cart and orders.
  function injectProperties(warning, vehicleName) {
    clearInjectedProperties();
    var form = document.querySelector('product-form form.form');
    if (!form) return;

    var carInput = document.createElement('input');
    carInput.type = 'hidden';
    carInput.name = 'properties[Car]';
    carInput.value = vehicleName;
    carInput.setAttribute('data-clickable-fitment-property', '');
    form.appendChild(carInput);

    if (warning) {
      var warnInput = document.createElement('input');
      warnInput.type = 'hidden';
      warnInput.name = 'properties[Warning]';
      warnInput.value = warning;
      warnInput.setAttribute('data-clickable-fitment-property', '');
      form.appendChild(warnInput);
    }
  }

  function paintMessageSlot(helpful, warning, verdict) {
    var slot = document.querySelector('[data-message-slot]');
    if (!slot) return;
    /* PDP option H (Lucas ruling 21 Aug): server messages are permanent —
       the wipe RE-LATCHES latents (helpful/warning) and leaves the
       CSS-gated unset prompt alone; only JS-created nodes are removed.
       Reveal = removing data-ck-latent on the text-matched latent: an
       in-place visibility flip at identical geometry. */
    slot.querySelectorAll('[data-ck-server-msg]').forEach(function (el) {
      if (!el.hasAttribute('data-ck-unset-msg')) {
        el.setAttribute('data-ck-latent', '');
        el.removeAttribute('data-ck-match'); /* stale pre-paint mark dies with its state */
      }
      el.removeAttribute('hidden');
    });
    Array.prototype.slice.call(slot.children).forEach(function (el) {
      /* server nodes and their latent-stack containers stay; JS-created go */
      if (el.hasAttribute('data-ck-server-msg') || el.classList.contains('ck-latent-stack')) return;
      el.remove();
    });
    slot.classList.toggle('has-both', !!(helpful && warning));
    function revealLatentMsg(cls, text) {
      var found = null;
      slot.querySelectorAll('.' + cls + '[data-ck-server-msg]').forEach(function (el) {
        if (found) return;
        var note = el.querySelector('span:last-child');
        if (note && note.textContent.trim() === String(text).trim()) found = { chip: el, note: note };
      });
      if (!found) return false;
      found.chip.removeAttribute('data-ck-latent');
      if (!found.chip.hasAttribute('data-ck-hl') && window.clickableHighlightFitmentDates) {
        window.clickableHighlightFitmentDates(found.note);
        found.chip.setAttribute('data-ck-hl', '');
      }
      return true;
    }
    /* red recomposes the column — server latents must not hold space
       above the red line; the [hidden] belt takes them out entirely
       (the re-latch above restores them on the next repaint). */
    if (verdict === 'red') {
      slot.querySelectorAll('[data-ck-server-msg]').forEach(function (el) { el.setAttribute('hidden', ''); });
    }

    // Canon band icons -- STATIC markup only; all data via textContent.
    var INFO_SVG = '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" aria-hidden="true"><circle cx="12" cy="12" r="9"/><path d="M12 8h.01M12 12v4"/></svg>';
    function glyph(chr) {
      var g = document.createElement('span');
      g.className = 'clickable-pdp-message__glyph';
      g.setAttribute('aria-hidden', 'true');
      g.textContent = chr;
      return g;
    }
    // Canon fitment warn ICON (5f, char -> icon swap) -- the same filled
    // triangle the card amber line paints (clickable-card-fitment.js).
    // fill:currentColor inherits the amber the --warning glyph rule sets.
    function warnGlyph() {
      var g = document.createElement('span');
      g.className = 'clickable-pdp-message__glyph';
      g.setAttribute('aria-hidden', 'true');
      g.innerHTML = '<svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor" stroke="none" aria-hidden="true"><path d="M12 2 L22 20 L2 20 Z"></path><rect x="11" y="9" width="2" height="6" fill="#fff"></rect><rect x="11" y="16" width="2" height="2" fill="#fff"></rect></svg>';
      return g;
    }
    function textSpan() {
      var sp = document.createElement('span');
      return sp;
    }
    if (verdict === 'red') {
      var red = document.createElement('p');
      red.className = 'clickable-pdp-message clickable-pdp-message--red';
      red.appendChild(glyph('\u2715'));
      var rBody = textSpan();
      var rLead = document.createElement('strong');
      rLead.textContent = "This part doesn't fit your " + shortName(state.name) + '.';
      rBody.appendChild(rLead);
      red.appendChild(rBody);
      slot.appendChild(red);
      return;
    }
    if (verdict === 'generic') {
      // generic + no vehicle: clean page — no prompt, no claims.
      return;
    }
    if (helpful && !revealLatentMsg('clickable-pdp-message--helpful', helpful)) {
      var teal = document.createElement('p');
      teal.className = 'clickable-pdp-message clickable-pdp-message--helpful';
      teal.insertAdjacentHTML('afterbegin', INFO_SVG);
      var tBody = textSpan();
      tBody.textContent = helpful;
      if (window.clickableHighlightFitmentDates) window.clickableHighlightFitmentDates(tBody);
      teal.appendChild(tBody);
      slot.appendChild(teal);
    }
    if (warning && revealLatentMsg('clickable-pdp-message--warning', warning)) {
      /* revealed in place — nothing to insert */
    } else if (warning) {
      var amber = document.createElement('p');
      amber.className = 'clickable-pdp-message clickable-pdp-message--warning';
      amber.appendChild(warnGlyph());
      var aBody = textSpan();
      // PDP sweep (Lucas, 11 Aug): the ATC-adjacent warning drops the
      // "Check before ordering:" prefix — clean warning text HERE ONLY
      // (the 01b copy conflict resolved for this surface; the prefix
      // stays everywhere else: fits-box band, cart/drawer notes, toast).
      aBody.appendChild(document.createTextNode(warning));
      if (window.clickableHighlightFitmentDates) window.clickableHighlightFitmentDates(aBody);
      amber.appendChild(aBody);
      slot.appendChild(amber);
    }
    if (!helpful && !warning && verdict === 'unset') {
      /* PDP option H: the unset prompt is server-rendered + CSS-gated on
         the stamp — creation survives only as the missing-node fallback. */
      if (slot.querySelector('[data-ck-unset-msg]')) return;
      // Canon copy (GenericFitmentPrompt) — supersedes the live-parity line
      // (register: changed per canon; copy pass may adjust).
      var unset = document.createElement('p');
      unset.className = 'clickable-pdp-message clickable-pdp-message--unset';
      var uLead = document.createElement('strong');
      uLead.textContent = 'Vehicle not set';
      unset.appendChild(uLead);
      unset.appendChild(document.createTextNode(' — enter your vehicle above to confirm this part fits before ordering.'));
      slot.appendChild(unset);
    }
  }

  // Hard block (contract, live parity): in the mismatch state the product
  // form and quantity are fully ABSENT — no purchase path. The red band /
  // in-column CTA actions are the only exits. No acknowledgement gate exists.
  function setHardBlock(active) {
    ['product-form.clickable-pdp__form', '.clickable-pdp__notify', '.clickable-pdp__wishlist', '.wishlist-engine'].forEach(function (selector) {
      var el = document.querySelector(selector);
      if (!el) return;
      if (active) el.setAttribute('hidden', '');
      else el.removeAttribute('hidden');
    });
    // In-column CTA (canon FindFittingPartsCTA) shows ONLY while blocked.
    var cta = document.querySelector('[data-red-cta]');
    if (cta) {
      if (active) cta.removeAttribute('hidden');
      else cta.setAttribute('hidden', '');
      var vehicleEl = cta.querySelector('[data-red-cta-vehicle]');
      if (vehicleEl) vehicleEl.textContent = state.name ? shortName(state.name) : '';
      var link = cta.querySelector('[data-red-cta-link]');
      if (link) {
        var base = link.getAttribute('data-base-href') || link.getAttribute('href');
        link.setAttribute('data-base-href', base);
        var tag = window.clickableVehicleState ? window.clickableVehicleState.getTag() : null;
        link.setAttribute('href', active && tag ? base + '/' + tag : base);
      }
    }
  }

  function paintRedBand(active) {
    var band = document.querySelector('[data-red-band]');
    if (band) {
      if (active) band.removeAttribute('hidden');
      else band.setAttribute('hidden', '');
      var vehicle = band.querySelector('[data-red-band-vehicle]');
      if (vehicle) vehicle.textContent = state.name ? shortName(state.name) : '';
    }
    // §43: the red explanation REPLACES the vehicle-context band.
    var browsingBar = document.querySelector('[data-vehicle-pill]');
    if (browsingBar) {
      if (active) browsingBar.setAttribute('hidden', '');
      else if (state.name) browsingBar.removeAttribute('hidden');
    }
    /* b117 item 1: keep the pre-paint red stamp (ckRedStamp) in sync so the
       stamp-keyed pill hide never outlives the verdict */
    if (active) document.documentElement.setAttribute('data-ck-fit', 'red');
    else document.documentElement.removeAttribute('data-ck-fit');
  }

  // Confirmed-fit card rows: reveal the vehicle's matching body/build rows
  // (canon: picker has no body/build, the customer self-identifies).
  function paintFitCard(verdict) {
    var rowsWrap = document.querySelector('[data-fitcard-rows]');
    if (!rowsWrap) return;
    var anyVisible = false;
    var anyWarning = false;
    rowsWrap.querySelectorAll('[data-fitcard-row]').forEach(function (row) {
      var match = sameVehicle(row.getAttribute('data-fitcard-model'), state.name) &&
        (verdict === 'fit');
      row.classList.toggle('is-visible', match);
      if (match) {
        row.removeAttribute('hidden');
        anyVisible = true;
        if (row.querySelector('.clickable-fitcard__inset--critical')) anyWarning = true;
      } else {
        row.setAttribute('hidden', '');
      }
    });
    if (anyVisible) rowsWrap.removeAttribute('hidden');
    else rowsWrap.setAttribute('hidden', '');
    var nuance = rowsWrap.querySelector('[data-fitcard-nuance]');
    if (nuance) {
      if (anyWarning) nuance.removeAttribute('hidden');
      else nuance.setAttribute('hidden', '');
    }
  }

  // FITMENTS accordion: matched group floats to the top, opens, carries the
  // YOUR CAR pill; matched rows tick + highlight + auto-reveal their warning.
  function setGroupOpen(group, open) {
    group.classList.toggle('is-open', open);
    var header = group.querySelector('[data-fitacc-toggle]');
    var panel = group.querySelector('[data-fitacc-panel]');
    if (header) header.setAttribute('aria-expanded', open ? 'true' : 'false');
    if (panel) {
      if (open) panel.removeAttribute('hidden');
      else panel.setAttribute('hidden', '');
    }
  }

  function paintAccordion(verdict, matchedGroupName) {
    var acc = document.querySelector('[data-fitacc]');
    if (!acc) return;
    var browsing = acc.querySelector('[data-fitacc-browsing]');
    if (browsing) {
      if (verdict === 'unset') browsing.removeAttribute('hidden');
      else browsing.setAttribute('hidden', '');
    }
    var container = acc.querySelector('.clickable-fitacc__groups');
    var matchedEl = null;
    acc.querySelectorAll('[data-fitacc-group]').forEach(function (group) {
      var isMatch = !!matchedGroupName && group.getAttribute('data-fitacc-group') === matchedGroupName;
      group.classList.toggle('is-match', isMatch);
      var pill = group.querySelector('[data-fitacc-yourcar]');
      if (pill) {
        if (isMatch) pill.removeAttribute('hidden');
        else pill.setAttribute('hidden', '');
      }
      setGroupOpen(group, isMatch);
      if (isMatch) matchedEl = group;
    });
    if (matchedEl && container && container.firstElementChild !== matchedEl) {
      container.insertBefore(matchedEl, container.firstElementChild);
    }
    // Row highlight (round-two addition 3): EVERY row whose model matches
    // the active vehicle highlights -- all body-style windows, same
    // principle as the confirmed-fit box. CONTRACT UNCHANGED: first-match-
    // wins still governs which row's warning feeds the banner and
    // properties[Warning] (state.matchIndex; recorded in Build Ref sec 1a).
    var models = state.data && Array.isArray(state.data.model) ? state.data.model : [];
    acc.querySelectorAll('[data-fitment-row]').forEach(function (row) {
      var idx = parseInt(row.getAttribute('data-fitment-row'), 10);
      var matched = verdict === 'fit' && sameVehicle(models[idx], state.name);
      row.classList.toggle('is-matched', matched);
      var tick = row.querySelector('[data-row-tick]');
      if (tick) {
        if (matched) tick.removeAttribute('hidden');
        else tick.setAttribute('hidden', '');
      }
      // Matched rows surface their warning expanded by default (canon).
      var warnBtn = row.querySelector('[data-warn-toggle]');
      var warnText = row.querySelector('[data-warn-text]');
      var warnHide = row.querySelector('[data-warn-hide]');
      if (warnBtn && warnText && matched) {
        warnBtn.setAttribute('hidden', '');
        warnText.removeAttribute('hidden');
        row.classList.add('is-warnopen');
        if (warnHide) warnHide.setAttribute('hidden', '');
      }
    });
  }

  function paint() {
    // CANONICAL CONTRACT (Task 04c §A): TAGS ALONE decide fit — and the
    // verdict comes ONLY from the state module's choke point
    // (FITMENT-HARDENING-01): below full-model state fitVerdict returns
    // 'unset' and this page composes the no-vehicle treatment. state.name
    // is whatever the choke point returns — never a partial context.
    var fv = window.clickableVehicleState
      ? window.clickableVehicleState.fitVerdict(state.tags)
      : { verdict: 'unset', name: null, tag: null };
    state.name = fv.name;
    var fitsByTag = fv.verdict === 'fit';
    state.matchIndex = state.data ? firstMatchIndex(state.data.model, state.name) : -1;
    var modelMatched = state.matchIndex !== -1;
    var warning = modelMatched ? entryAt(state.data.warning, state.matchIndex) : '';
    var helpful = modelMatched ? entryAt(state.data.helpful, state.matchIndex) : '';

    // Drift logger (§B): tag verdict vs accentuate lookup disagreement —
    // migration evidence for October's stable-ID rebuild. No UI.
    if (state.name && fitsByTag !== modelMatched) {
      var handle = document.querySelector('[data-pdp-root]');
      handle = handle ? handle.getAttribute('data-product-handle') : window.location.pathname;
      console.info('[fitment-drift] ' + handle + ': ' + (fitsByTag
        ? 'tag-fit without accentuate row (no warning/table detail available)'
        : 'accentuate row without tag (red by canon)'));
    }

    // GENERIC RULE (Lucas, 11 Aug): the generic tag's only effect is in
    // the NO-VEHICLE state — it suppresses the enter-your-vehicle
    // prompt/tip/gate (clean, purchasable page). Once a vehicle exists,
    // TAGS are the sole fitment truth for every product, generic
    // included: verified messaging keys off the tag match, red off its
    // absence. The fitment table renders when its data exists and is
    // silently absent otherwise.
    var verdict = (state.generic && fv.verdict === 'unset') ? 'generic' : fv.verdict;

    paintMessageSlot(helpful, warning, verdict);
    paintRedBand(verdict === 'red');
    setHardBlock(verdict === 'red');
    document.body.classList.toggle('pdp-unset', verdict === 'unset');

    // Confirmed-fit card band + rows.
    if (verdict === 'generic') {
      showBox('none');
    } else if (verdict === 'unset') {
      showBox('unset');
    } else if (verdict === 'red') {
      showBox('red');
      document.querySelectorAll('[data-fit-vehicle-nomatch]').forEach(function (el) {
        el.textContent = shortName(state.name);
      });
    } else if (warning) {
      showBox('warning');
      var warnText = document.querySelector('[data-fit-warning-text]');
      if (warnText) {
        warnText.textContent = warning;
        if (window.clickableHighlightFitmentDates) window.clickableHighlightFitmentDates(warnText);
      }
      var warnVehicle = document.querySelector('[data-fit-vehicle-warn]');
      if (warnVehicle) warnVehicle.textContent = shortName(state.name);
    } else {
      showBox('fit');
      document.querySelectorAll('[data-fit-vehicle]').forEach(function (el) {
        el.textContent = shortName(state.name);
      });
    }
    paintFitCard(verdict);
    paintObw();
    paintAccordion(verdict, verdict === 'fit' && modelMatched ? groupOf(state.data.model[state.matchIndex]) : null);

    // Line-item properties: fit carries Car (+Warning). Red has no purchase
    // path, so nothing is ever injected there.
    // NO-VEHICLE SENTINEL (Lucas ruling 27 Aug): a fitted (non-generic)
    // product added with no vehicle carries Car = the sentinel below, so
    // checkout, the confirmation email and the ADMIN ORDER record that
    // fitment was never confirmed (previously the line carried nothing).
    // 'unset' IS the no-vehicle state on non-generic products (generic
    // rebrands to 'generic' above and stays silent, 13 Aug ruling).
    // Every display surface classifies the sentinel as UNVERIFIED by the
    // full-model shape checks (cart page + drawer: Car contains '(';
    // toast: clickableVehicleLevel guard) -- the amber not-verified voice
    // stays the voice, the green pill never paints it.
    if (verdict === 'fit') {
      injectProperties(warning, state.name);
    } else if (verdict === 'unset') {
      injectProperties('', 'Vehicle unknown — fitment not confirmed');
    } else {
      clearInjectedProperties();
    }
    var reminder = document.querySelector('[data-fitment-reminder]');
    if (reminder) {
      if (warning) reminder.removeAttribute('hidden');
      else reminder.setAttribute('hidden', '');
    }
    document.body.classList.toggle('warning-active', !!warning || verdict === 'red');
  }

  // Category crumb (round two): the 07b family+value form, vehicle-scoped —
  // /collections/{family}/{vehicle-seg}+{tax-seg} when a vehicle is set,
  // plain family+value otherwise. 2-segment crumbs (brand excluded, family
  // included) belong to the GLOBAL decorator; this owns only the 3-segment
  // category crumb (double-decoration bug retired with the old back-link
  // appender).
  function decorateCategoryCrumb() {
    var link = document.querySelector('[data-crumb-tax]');
    if (!link) return;
    var base = link.getAttribute('data-crumb-base');
    var seg = link.getAttribute('data-crumb-tax');
    // Context tag (verified first, else browse scope): crumb scoping is
    // listing filtration, a power browse context keeps (hardening ruling).
    var tag = window.clickableVehicleState ? window.clickableVehicleState.getContextTag() : null;
    var veh = tag ? encodeURIComponent(tag).replace(/%2F/gi, '/') : null;
    link.setAttribute('href', base + '/' + (veh ? veh + '+' : '') + seg);
  }

  document.addEventListener('click', function (event) {
    var toggle = event.target.closest && event.target.closest('[data-fitacc-toggle]');
    if (toggle) {
      var group = toggle.closest('[data-fitacc-group]');
      if (group) setGroupOpen(group, !group.classList.contains('is-open'));
      return;
    }
    var warnBtn = event.target.closest && event.target.closest('[data-warn-toggle]');
    if (warnBtn) {
      var row = warnBtn.closest('[data-fitment-row]');
      warnBtn.setAttribute('hidden', '');
      var text = row && row.querySelector('[data-warn-text]');
      if (text) text.removeAttribute('hidden');
      if (row) row.classList.add('is-warnopen');
      return;
    }
    var warnHide = event.target.closest && event.target.closest('[data-warn-hide]');
    if (warnHide) {
      var hideRow = warnHide.closest('[data-fitment-row]');
      var hideText = warnHide.closest('[data-warn-text]');
      if (hideText) hideText.setAttribute('hidden', '');
      var btn = hideRow && hideRow.querySelector('[data-warn-toggle]');
      if (btn) btn.removeAttribute('hidden');
      if (hideRow) hideRow.classList.remove('is-warnopen');
      return;
    }
    if (event.target.hasAttribute && event.target.hasAttribute('data-scroll-to-quickfinder')) {
      var qf = document.querySelector('[data-qf-card]');
      if (qf) qf.scrollIntoView({ behavior: 'smooth', block: 'center' });
      return;
    }
    // "Change vehicle" (red state): clear via the state module, then open
    // the QuickFinder. Clearing flips the page to the no-vehicle state.
    if (event.target.hasAttribute && event.target.hasAttribute('data-change-vehicle')) {
      if (window.clickableVehicleState) window.clickableVehicleState.clearVehicle();
      var finder = document.querySelector('[data-qf-card]');
      if (finder) finder.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }
  });

  // OFTEN BOUGHT WITH (canon: companions only, never alternatives).
  // Vehicle-aware per the 11 Aug ruling: no vehicle -> all curated
  // companions + the generic subtitle; verified vehicle -> only companions
  // whose tags fit (verdicts via the choke point, never derived) + the
  // vehicle subtitle. All companions filtered out -> the aside collapses
  // and the fitment table takes the full pair width (visibility flips are
  // user-action-driven -- not CLS-counted).
  function paintObw() {
    var aside = document.querySelector('.clickable-pdpfit__obw');
    var pair = document.querySelector('.clickable-pdpfit');
    if (!aside || !pair) return;
    var listEl = aside.querySelector('[data-obw-list]');
    var subtitle = aside.querySelector('[data-obw-subtitle]');
    if (!listEl) return;
    var limit = parseInt(listEl.getAttribute('data-obw-limit'), 10) || 5;
    var fv = window.clickableVehicleState ? window.clickableVehicleState.fitVerdict([]) : { verdict: 'unset' };
    var verified = fv.name || null;

    // FAIL-CLOSED: selection happens over the in-memory pool; the DOM only
    // ever receives the first <=limit qualifying candidates (self was
    // excluded at pool build). No pool yet (fetch pending/failed): leave
    // the skeleton alone -- the CSS autohide owns the failure look.
    if (!Array.isArray(state.obwPool)) return;
    var chosen = [];
    for (var i = 0; i < state.obwPool.length && chosen.length < limit; i++) {
      var cand = state.obwPool[i];
      if (verified) {
        var candFv = window.clickableVehicleState.fitVerdict(cand.tags);
        if (candFv.verdict !== 'fit') continue;
      }
      chosen.push(cand);
    }

    var frag = document.createDocumentFragment();
    chosen.forEach(function (cand) {
      var row = document.createElement('a');
      row.className = 'clickable-pdpfit__obw-row';
      row.href = cand.url;
      row.setAttribute('data-obw-row', '');
      var name = document.createElement('span');
      name.className = 'clickable-pdpfit__obw-name';
      name.textContent = cand.title;
      var price = document.createElement('span');
      price.className = 'clickable-pdpfit__obw-price';
      price.textContent = cand.price;
      row.appendChild(name);
      row.appendChild(price);
      frag.appendChild(row);
    });
    listEl.textContent = '';
    listEl.appendChild(frag);
    listEl.style.minHeight = '';

    if (subtitle) {
      var generic = subtitle.getAttribute('data-obw-generic') || '';
      var vehicleTpl = subtitle.getAttribute('data-obw-vehicle') || '';
      var text = verified && vehicleTpl
        ? vehicleTpl.replace('{vehicle}', shortName(verified))
        : generic;
      if (subtitle.textContent !== text) subtitle.textContent = text;
    }
    var collapsed = chosen.length === 0;
    if (collapsed) aside.setAttribute('hidden', '');
    else aside.removeAttribute('hidden');
    aside.classList.add('obw-ready');
    pair.classList.toggle('has-obw', !collapsed);
  }

  // RELATED-PARTS candidates (final ruling, 11 Aug): the LIVE selection
  // logic, quirks fixed -- Storefront query of the 'store' collection
  // narrowed SERVER-SIDE by the underscore category tag (live filtered by
  // vehicle server-side and category client-side over a 250 cap; binding
  // the cap per category keeps the pool complete for every real category
  // size). Self excluded at BUILD time, before any slicing (live sliced 4
  // then skipped self mid-render). Vehicle filtering stays client-side in
  // paintObw via the choke point (live read raw localStorage). The public
  // storefront token is the same one the live theme embeds.
  function obwInit() {
    var listEl = document.querySelector('[data-obw-list]');
    if (!listEl) return;
    var cat = listEl.getAttribute('data-obw-cat');
    var exclude = listEl.getAttribute('data-obw-exclude');
    if (!cat) return;
    var query = '{ collection(handle: "store") { products(first: 250, filters: [{ tag: ' + JSON.stringify(cat) + ' }]) { edges { node { handle title tags priceRange { minVariantPrice { amount } } } } } } }';
    fetch('/api/2023-07/graphql.json', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'X-Shopify-Storefront-Access-Token': '442b4c3bbc9518ec6b52e2d1a78115fa'
      },
      body: JSON.stringify({ query: query })
    }).then(function (r) { return r.json(); }).then(function (payload) {
      var edges = payload && payload.data && payload.data.collection && payload.data.collection.products
        ? payload.data.collection.products.edges : [];
      var pool = [];
      edges.forEach(function (edge) {
        var n = edge.node;
        if (!n || n.handle === exclude) return; // self OUT at pool build
        var amount = parseFloat(n.priceRange && n.priceRange.minVariantPrice ? n.priceRange.minVariantPrice.amount : '');
        pool.push({
          handle: n.handle,
          title: n.title,
          tags: n.tags || [],
          url: '/products/' + n.handle,
          price: isNaN(amount) ? '' : '$' + (amount % 1 === 0 ? amount.toFixed(0) : amount.toFixed(2))
        });
      });
      state.obwPool = pool;
      paintObw();
    }).catch(function () { /* fetch failure: pool stays null, CSS autohide owns the look */ });
  }

  // In-place resolve reveal (context rule, 11 Aug): scrolls the verdict
  // surfaces into view after an on-page QuickFinder submission and pulses
  // the message slot (CSS class, opacity-only -- no CLS).
  window.clickableFitmentReveal = function () {
    var target = document.querySelector('[data-message-slot]') || document.querySelector('[data-fit-box]:not([hidden])');
    if (!target) return;
    requestAnimationFrame(function () {
      target.scrollIntoView({ behavior: 'smooth', block: 'center' });
      target.classList.remove('clickable-fitment-pulse');
      void target.offsetWidth;
      target.classList.add('clickable-fitment-pulse');
    });
  };

  document.addEventListener('clickable:vehicle-changed', function () {
    // paint() pulls name + verdict from the choke point itself; the event
    // detail is deliberately not consumed (it can never carry a partial,
    // but the single source stays the module).
    decorateCategoryCrumb();
    paint();
  });

  document.addEventListener('DOMContentLoaded', function () {
    state.data = readData();
    state.tags = readTags();
    var root = document.querySelector('[data-pdp-root]');
    state.generic = !!(root && root.hasAttribute('data-generic'));
    decorateCategoryCrumb();
    paint();
    obwInit();
  });
})();
